import React, { useEffect } from 'react';

import { SEO } from '../../shared/components/SEO';
import WhatsAppFloatingButton from '../WhatsAppFloatingButton';
import MarketingLandingNav from '../marketing/MarketingLandingNav';
import MarketingPageFooter from '../marketing/MarketingPageFooter';
import DripfyStyledHero from './sections/DripfyStyledHero';
import HeroPlatformLogoWall from './sections/HeroPlatformLogoWall';
import HomeHowItWorksStepperSection from './sections/HomeHowItWorksStepperSection';
import HomeInteractiveDemoSection from './sections/HomeInteractiveDemoSection';
import HomeHowItWorksSection from './sections/HomeHowItWorksSection';
import HomeSocialProofSection from './sections/HomeSocialProofSection';
import FinalCTASimple from './sections/FinalCTASimple';

interface HomePageProps {
  onNavigate?: (view: 'home' | 'login' | 'signup' | 'contact-us') => void;
}

const HOME_SECTION_IDS = [
  'hero',
  'how-it-works',
  'interactive-demo',
  'comparison',
  'social-proof',
  'cta',
] as const;

const HomePage: React.FC<HomePageProps> = ({ onNavigate }) => {
  useEffect(() => {
    const scrollToHash = () => {
      const id = window.location.hash.replace(/^#/, '');
      if (!id || !HOME_SECTION_IDS.includes(id as (typeof HOME_SECTION_IDS)[number])) return;
      requestAnimationFrame(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    };

    scrollToHash();
    window.addEventListener('hashchange', scrollToHash);
    return () => window.removeEventListener('hashchange', scrollToHash);
  }, []);

  return (
    <div className="home-page home-page--funnel min-h-screen overflow-x-visible overflow-y-visible bg-white text-gray-900 transition-colors duration-300 dark:bg-[#19191A] dark:text-white">
      <SEO
        title="AI Job Search with Smart Recommendations | GoGrabJob"
        description="AI finds job matches, prepares tailored application packs, and scores your resume — you review and submit. Free ATS check, no credit card required."
        canonical="https://www.gograbjob.com/"
        includeGeo
      />

      <MarketingLandingNav onNavigate={onNavigate as (view: string) => void} variant="funnel" />

      <div className="home-hero-chrome">
        <DripfyStyledHero onNavigate={onNavigate} />
        <HeroPlatformLogoWall />
      </div>

      <HomeHowItWorksStepperSection onNavigate={onNavigate} />

      <HomeInteractiveDemoSection onNavigate={onNavigate} />

      <HomeHowItWorksSection onNavigate={onNavigate} />

      <HomeSocialProofSection
        onNavigate={onNavigate}
        maxStories={3}
        layout="grid"
        showCta={false}
        showViewAllLink
      />

      <FinalCTASimple onNavigate={onNavigate} scrollToHero />

      <MarketingPageFooter onNavigate={onNavigate as never} theme="home" />

      <WhatsAppFloatingButton />
    </div>
  );
};

export default React.memo(HomePage);
